import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const { confirm: _c, ...payload } = form;
      const u = await register(payload);
      nav(u.role === "patient" ? "/m" : "/", { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-stone-300 bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none";

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4 py-12">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl shadow-soft p-8">
        <Link to="/login" className="block mb-6 text-brand-700"><Logo size={32} withWordmark /></Link>
        <h1 className="font-display text-2xl font-bold text-stone-900">Create patient account</h1>
        <p className="text-stone-500 mt-1 text-sm">Track your treatment, appointments and side-effects in one place — My OncoCadence.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          {[
            { k: "full_name", l: "Full Name", t: "text", req: true },
            { k: "email", l: "Email", t: "email", req: true },
            { k: "phone", l: "Phone (optional)", t: "tel", req: false },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{f.l}</label>
              <input data-testid={`register-${f.k}-input`} type={f.t} required={f.req} value={form[f.k]} onChange={upd(f.k)} className={inputCls} />
            </div>
          ))}
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Password (min 8)</label>
            <div className="relative">
              <input data-testid="register-password-input" type={show ? "text" : "password"} required minLength={8} value={form.password} onChange={upd("password")} className={`${inputCls} pr-10`} />
              <button type="button" onClick={() => setShow((v) => !v)} data-testid="toggle-password" aria-label={show ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-stone-500 hover:text-stone-800">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Confirm Password</label>
            <div className="relative">
              <input data-testid="register-confirm-input" type={showConfirm ? "text" : "password"} required minLength={8} value={form.confirm} onChange={upd("confirm")} className={`${inputCls} pr-10`} />
              <button type="button" onClick={() => setShowConfirm((v) => !v)} data-testid="toggle-confirm" aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-stone-500 hover:text-stone-800">
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {form.confirm && form.confirm !== form.password && (
              <div className="text-xs text-rose-600 mt-1" data-testid="confirm-mismatch">Passwords do not match.</div>
            )}
          </div>
          {err && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />{err}
            </div>
          )}
          <button data-testid="register-submit-button" type="submit" disabled={busy} className="w-full py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold transition-colors disabled:opacity-60">
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>
        <div className="mt-6 text-sm text-stone-500 text-center">
          Already have an account? <Link to="/login" className="text-brand-600 hover:text-brand-700 font-semibold">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
